import {
    DateField,
    EditButton,
    ReferenceField,
    Show,
    TextField
} from '@/components/admin';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CheckCircle, DollarSign, Eye, EyeOff, Mail, Truck } from 'lucide-react';
import { useDataProvider, useNotify, useRecordContext } from 'ra-core';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useConfigurationContext } from '../../root/ConfigurationContext';
import { AddressBlock } from '../shared/AddressBlock';
import { DocumentPDFButton } from '../shared/DocumentPDFButton';
import { StatusBadge } from '../shared/StatusBadge';
import { WatermarkOverlay } from '../shared/WatermarkOverlay';
import { InvoiceEmailModal } from './InvoiceEmailModal';
import { InvoicePaymentModal } from './InvoicePaymentModal';
import { ThreeWayMatchBadge } from './ThreeWayMatchBadge';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Arial:wght@400;700&display=swap');

  .po-wrap {
    font-family: Arial, sans-serif;
    font-size: 12px;
    color: #000;
    background: #e8eaf0;
    min-height: 100vh;
    padding: 28px 20px 48px;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .po-sheet {
    background: #fff;
    max-width: 960px;
    margin: 0 auto;
    padding: 20px 24px 24px;
    box-shadow: 0 2px 16px rgba(0,0,0,0.12);
    border: 1px solid #ccc;
    position: relative;
  }

  .po-header-bar {
    display: grid;
    grid-template-columns: 200px 1fr 220px;
    align-items: center;
    border: 1.5px solid #000;
  }
  .po-header-logo { padding: 10px 12px; border-right: 1.5px solid #000; }
  .po-header-logo img { width: 160px; height: auto; display: block; }
  .po-header-title {
    text-align: center;
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 10px;
  }
  .po-header-meta { border-left: 1.5px solid #000; padding: 8px 12px; font-size: 12px; }
  .po-meta-row {
    display: grid;
    grid-template-columns: 80px 10px 1fr;
    gap: 4px;
    margin-bottom: 4px;
    font-size: 11px;
    align-items: center;
  }
  .po-meta-lbl { font-weight: 700; white-space: nowrap; text-transform: uppercase; color: #555; }
  .po-meta-val { font-weight: 700; color: #000; }

  .from-to-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    border: 1.5px solid #000;
    border-top: none;
  }
  .ft-col { border-right: 1.5px solid #000; display: flex; flex-direction: column; }
  .ft-col:last-child { border-right: none; }
  .ft-label {
    font-weight: 700; font-size: 11px; background: #f0f0f0;
    padding: 4px 10px; border-bottom: 1px solid #ccc;
    text-transform: uppercase; letter-spacing: 0.06em;
  }
  .ft-body { padding: 8px 12px; font-size: 11px; line-height: 1.6; flex: 1; }
  .ft-body strong { font-size: 12px; display: block; margin-bottom: 3px; }
  .ft-row { display: flex; gap: 4px; margin-bottom: 1px; }
  .ft-row-lbl { font-weight: 700; white-space: nowrap; min-width: 100px; }

  .ship-to-bar { border: 1.5px solid #000; border-top: none; }
  .ship-to-inner { display: grid; grid-template-columns: 160px 1fr; }
  .ship-to-lbl {
    font-weight: 700; font-size: 11px; padding: 6px 10px;
    border-right: 1.5px solid #000; background: #f0f0f0;
    display: flex; align-items: flex-start;
    text-transform: uppercase; letter-spacing: 0.06em;
  }
  .ship-to-body { padding: 6px 12px; font-size: 11px; line-height: 1.6; }

  .dear-sir { padding: 8px 0 4px; font-size: 12px; }

  .items-tbl { width: 100%; border-collapse: collapse; border: 1.5px solid #000; font-size: 11px; }
  .items-tbl thead tr { background: #e8e8e8; }
  .items-tbl th {
    padding: 7px 6px; font-size: 11px; font-weight: 700;
    text-align: center; border: 1px solid #000;
    text-transform: uppercase; letter-spacing: 0.03em;
  }
  .items-tbl th.left { text-align: left; }
  .items-tbl td { padding: 8px 6px; border: 1px solid #000; text-align: center; vertical-align: middle; }
  .items-tbl td.left { text-align: left; }
  .items-tbl td.right { text-align: right; }
  .item-desc-main { font-weight: 700; font-size: 12px; }

  .totals-section { border: 1.5px solid #000; border-top: none; display: grid; grid-template-columns: 1fr 280px; }
  .banking-details { padding: 8px 12px; font-size: 11px; border-right: 1.5px solid #000; }
  .banking-details b { font-size: 12px; display: block; margin-bottom: 4px; }
  .totals-right { display: flex; flex-direction: column; }
  .t-row { display: flex; justify-content: space-between; padding: 5px 12px; border-bottom: 1px solid #ddd; font-size: 12px; }
  .t-row:last-child { border-bottom: none; }
  .t-row-lbl { font-weight: 700; color: #333; font-size: 11px; }
  .grand-total-bar { display: flex; justify-content: space-between; padding: 8px 12px; background: #1a1a1a; color: #fff; font-weight: 700; font-size: 12px; }

  .instructions-section { border: 1.5px solid #000; border-top: none; }
  .instr-row { display: flex; border-bottom: 1px solid #ccc; }
  .instr-row:last-child { border-bottom: none; }
  .instr-lbl { font-weight: 700; font-size: 11px; min-width: 140px; padding: 6px 12px; border-right: 1px solid #ccc; background: #f9f9f9; display: flex; align-items: flex-start; }
  .instr-val { padding: 6px 12px; font-size: 11px; line-height: 1.6; flex: 1; }

  .tc-section { border: 1.5px solid #000; border-top: none; padding: 10px 14px; font-size: 11px; line-height: 1.7; }
  .tc-title { font-weight: 700; font-size: 12px; margin-bottom: 6px; text-decoration: underline; }

  .signatory-section { border: 1.5px solid #000; border-top: none; display: grid; grid-template-columns: 1fr 1fr; }
  .signatory-col { padding: 8px 14px 12px; border-right: 1.5px solid #000; text-align: center; font-size: 11px; position: relative; }
  .signatory-col:last-child { border-right: none; }
  .signatory-space { height: 80px; display: flex; align-items: center; justify-content: center; position: relative; }
  .signatory-lbl { font-weight: 700; font-size: 12px; }
  .stamp-img { height: 70px; width: auto; object-fit: contain; opacity: 0.92; }
  .sign-img { height: 36px; width: auto; object-fit: contain; opacity: 0.9; position: absolute; bottom: 4px; right: 10px; }


  .po-footer-center { text-align: center; font-size: 11px; font-weight: 700; border: 1.5px solid #000; border-top: none; padding: 7px; letter-spacing: 0.06em; }
  .po-footer { margin-top: 6px; display: flex; justify-content: space-between; font-size: 10px; color: #666; }
  .status-row { display: flex; align-items: center; gap: 8px; margin-top: 4px; flex-wrap: wrap; }
  .payment-badge { font-size: 10px; font-weight: 700; padding: 2px 6px; border-radius: 4px; border: 1px solid #ccc; text-transform: uppercase; }

  /* ── PRINT STYLES ── */
  @media print {
    html, body {
      background: white !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    body * {
      visibility: hidden;
    }
    .po-wrap, .po-wrap * {
      visibility: visible;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .po-wrap {
      position: absolute;
      left: 0;
      top: 0;
      margin: 0;
      padding: 0;
      background: white;
      width: 100%;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .po-sheet {
      display: flex !important;
      flex-direction: column !important;
      min-height: calc(297mm - 16mm) !important;
      box-shadow: none;
      border: none !important;
      padding: 0 !important;
      margin: 0 !important;
      max-width: 100%;
    }
    .print-border {
      display: block !important;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border: 1.5px solid #000;
      pointer-events: none;
      z-index: 10000;
    }
    .print-spacer {
      display: block !important;
      flex-grow: 1 !important;
    }
    .status-row, .audit-trail {
      display: none !important;
    }
    .items-tbl tr {
      page-break-inside: avoid;
    }
    .totals-section, .signatory-section, .po-footer-center, .po-footer {
      page-break-inside: avoid;
      break-inside: avoid;
    }
    @page {
      margin: 8mm;
      size: A4;
    }
    .print-hide {
      display: none !important;
    }
    .address-block {
      margin-bottom: 0 !important;
      padding: 5px 10px !important;
    }
    .address-block * {
      font-size: 10px !important;
      line-height: 1.4 !important;
    }
    .items-tbl th, .items-tbl td {
      padding: 5px 6px !important;
      font-size: 10px !important;
      line-height: 1.35 !important;
    }
    .totals-section {
      grid-template-columns: 1fr 240px !important;
    }
    .totals-section .banking-details {
      padding: 6px 10px !important;
      font-size: 10px !important;
      line-height: 1.4 !important;
    }
    .totals-section .banking-details b {
      margin-bottom: 2px !important;
    }
    .totals-section .t-row {
      padding: 4px 10px !important;
      font-size: 11px !important;
    }
    .grand-total-bar {
      padding: 6px 10px !important;
      font-size: 12px !important;
    }
    .signatory-col {
      padding: 6px 10px !important;
    }
    .signatory-space {
      height: 50px !important;
    }
    .stamp-img {
      height: 45px !important;
    }
    .tc-section {
      padding: 4px 10px !important;
      font-size: 7.5px !important;
      line-height: 1.25 !important;
    }
    .tc-title {
      margin-bottom: 2px !important;
      font-size: 9px !important;
    }
    .po-header-logo {
      padding: 6px 10px !important;
    }
    .po-header-logo img {
      width: 125px !important;
    }
    .po-header-title {
      font-size: 15px !important;
      padding: 6px !important;
    }
    .po-header-meta {
      padding: 6px 10px !important;
    }
    .po-meta-row {
      margin-bottom: 2px !important;
      font-size: 10px !important;
    }
    .dear-sir {
      padding: 4px 0 !important;
      font-size: 11px !important;
    }
  }
`;

const inr = (val: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(val);

const amountInWords = (amount: number): string => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
        'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const numToWords = (n: number): string => {
        if (n === 0) return '';
        if (n < 20) return ones[n] + ' ';
        if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '') + ' ';
        if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred ' + numToWords(n % 100);
        if (n < 100000) return numToWords(Math.floor(n / 1000)) + 'Thousand ' + numToWords(n % 1000);
        if (n < 10000000) return numToWords(Math.floor(n / 100000)) + 'Lakh ' + numToWords(n % 100000);
        return numToWords(Math.floor(n / 10000000)) + 'Crore ' + numToWords(n % 10000000);
    };
    const wholePart = Math.floor(amount);
    const decimalPart = Math.round((amount - wholePart) * 100);
    let result = 'INR ' + numToWords(wholePart).trim() + ' Rupees';
    if (decimalPart > 0) result += ' and ' + numToWords(decimalPart).trim() + ' Paise';
    return result + ' Only';
};

const InvoiceShowTitle = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <div className="flex items-center gap-3">
            <Button asChild variant="outline" size="sm" className="gap-1.5 bg-[#00a68d]/10 hover:bg-[#00a68d]/20 text-[#00a68d] hover:text-[#00947e] border border-[#00a68d]/20 hover:border-[#00a68d]/30 font-semibold cursor-pointer h-9 px-4 rounded-lg">
                <Link to="/customer_invoices" className="flex items-center gap-1.5">
                    <ArrowLeft className="w-4 h-4 text-[#00a68d]" /> Back to Invoices
                </Link>
            </Button>
            <span className="text-xl font-bold tracking-tight">Tax Invoice {record.reference}</span>
        </div>
    );
};

const InvoiceShowActions = ({ onEmailClick, onPaymentClick, showWatermark, onToggleWatermark }: { onEmailClick: () => void, onPaymentClick: () => void, showWatermark: boolean, onToggleWatermark: () => void }) => {
    const record = useRecordContext();
    const navigate = useNavigate();
    const dataProvider = useDataProvider();
    const notify = useNotify();

    const handleApprove = async () => {
        if (!record) return;
        try {
            await dataProvider.update('customer_invoices', {
                id: record.id,
                data: { 
                    status: 'approved',
                    approved_at: new Date().toISOString()
                },
                previousData: record
            });
            notify('Invoice approved', { type: 'success' });
        } catch (error) {
            notify('Error approving invoice', { type: 'error' });
        }
    };

    const handleGenerateDC = () => {
        if (!record) return;
        navigate('/delivery_challans/create', {
            state: {
                record: {
                    company_id: record.company_id,
                    contact_id: record.customer_id,
                    project_id: record.project_id,
                    pi_id: record.id,
                    status: 'draft',
                    challan_type: 'Supply',
                    items: record.items?.map((item: any, index: number) => ({
                        sr_no: index + 1,
                        description: item.description,
                        quantity: item.quantity,
                        unit: item.unit || 'Nos',
                    })) || [],
                },
            },
        });
    };

    return (
        <div className="flex items-center gap-2">
            <Button
                variant={showWatermark ? "secondary" : "outline"}
                size="sm"
                onClick={onToggleWatermark}
                className={showWatermark ? "bg-emerald-600 text-white hover:bg-emerald-700" : ""}
            >
                {showWatermark ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
                Watermark: {showWatermark ? "ON" : "OFF"}
            </Button>
            <EditButton />
            <Button variant="outline" size="sm" onClick={onEmailClick}>
                <Mail className="w-4 h-4 mr-2" />
                Send Email
            </Button>
            <DocumentPDFButton title="Tax Invoice" record={record} />

            <Button
                variant="outline"
                size="sm"
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={handleGenerateDC}
            >
                <Truck className="w-4 h-4 mr-2" />
                Generate DC
            </Button>
            {record?.status === 'draft' && (
                <Button
                    variant="outline"
                    size="sm"
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={handleApprove}
                >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve Invoice
                </Button>
            )}
            {record?.status !== 'paid' && record?.status !== 'draft' && (
                <Button
                    variant="outline"
                    size="sm"
                    className="bg-green-600 text-white hover:bg-green-700 hover:text-white"
                    onClick={onPaymentClick}
                >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Record Payment
                </Button>
            )}
        </div>
    );
};

const InvoiceShowContent = ({ showWatermark }: { showWatermark: boolean }) => {
    const record = useRecordContext();
    const config = useConfigurationContext();
    if (!record) return null;

    const isOverdue = record.due_date
        && record.status !== 'paid'
        && record.status !== 'draft'
        && new Date(record.due_date) < new Date(new Date().setHours(0, 0, 0, 0));

    return (
        <>
            <style>{css}</style>
            <div className="po-wrap">
                <div className="po-sheet">
                    <div className="print-border" />
                    <WatermarkOverlay isVisible={showWatermark} />

                    {/* ── HEADER BAR ── */}
                    <div className="po-header-bar">
                        <div className="po-header-logo">
                            <img src="/logos/rovasys_horizontal_clean.png" alt="Rovasys" />
                            <div style={{ fontSize: 9, color: '#555', marginTop: 4, lineHeight: 1.4 }}>
                                W-145, S-Block, MIDC-Bhosari, Pune - 411026<br />
                                Ph: +91 91722 01844 | info@rovasys.com
                            </div>
                        </div>
                        <div className="po-header-title">Tax Invoice</div>
                        <div className="po-header-meta">
                            <div className="po-meta-row">
                                <span className="po-meta-lbl">REF NO</span>
                                <span>:</span>
                                <span className="po-meta-val">{record.reference}</span>
                            </div>
                            <div className="po-meta-row">
                                <span className="po-meta-lbl">Date</span>
                                <span>:</span>
                                <span className="po-meta-val"><DateField source="date" /></span>
                            </div>
                            {record.sales_order_id && (
                                <div className="po-meta-row">
                                    <span className="po-meta-lbl">Ref SO</span>
                                    <span>:</span>
                                    <span className="po-meta-val">
                                        <ReferenceField source="sales_order_id" reference="sales_orders" link="show">
                                            <TextField source="reference" />
                                        </ReferenceField>
                                    </span>
                                </div>
                            )}
                            {record.proforma_invoice_id && (
                                <div className="po-meta-row">
                                    <span className="po-meta-lbl">Ref PI</span>
                                    <span>:</span>
                                    <span className="po-meta-val">
                                        <ReferenceField source="proforma_invoice_id" reference="proforma_invoices" link={false}>
                                            <TextField source="reference" />
                                        </ReferenceField>
                                    </span>
                                </div>
                            )}
                            {record.po_id && (
                                <div className="po-meta-row">
                                    <span className="po-meta-lbl">Ref PO</span>
                                    <span>:</span>
                                    <span className="po-meta-val">
                                        <ReferenceField source="po_id" reference="purchase_orders" link="show">
                                            <TextField source="reference" />
                                        </ReferenceField>
                                    </span>
                                </div>
                            )}
                            {record.grn_id && (
                                <div className="po-meta-row">
                                    <span className="po-meta-lbl">Ref GRN</span>
                                    <span>:</span>
                                    <span className="po-meta-val">
                                        <ReferenceField source="grn_id" reference="goods_receipts" link="show">
                                            <TextField source="reference" />
                                        </ReferenceField>
                                    </span>
                                </div>
                            )}
                            <div className="po-meta-row">
                                <span className="po-meta-lbl">Status</span>
                                <span>:</span>
                                <span className="po-meta-val" style={{ textTransform: 'uppercase' }}>
                                    {record.status}
                                    {record.payment_status && <span style={{ marginLeft: 8, fontSize: '0.8em', opacity: 0.8 }}>({record.payment_status})</span>}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* ── FROM / TO ── */}
                    <div className="from-to-grid">
                        <AddressBlock 
                            label="Supplier Details"
                            addressOverride={
                                <>
                                    <strong>ROVASYS ALLIED WORKS PVT. LTD.</strong>
                                    W-145, S-Block, MIDC-Bhosari,<br />
                                    Pune, Maharashtra – 411026<br />
                                    <div className="ft-row"><span className="ft-row-lbl">GSTIN</span><span>: 27AAMCR6049R1ZD</span></div>
                                    <div className="ft-row"><span className="ft-row-lbl">PAN</span><span>: AAMCR6049R</span></div>
                                    <div className="ft-row"><span className="ft-row-lbl">State Code</span><span>: 27</span></div>
                                    <div className="ft-row"><span className="ft-row-lbl">Ph</span><span>: +91 91722 01844</span></div>
                                    <div className="ft-row"><span className="ft-row-lbl">E-mail</span><span>: info@rovasys.com</span></div>
                                </>
                            }
                        />

                        {/* TO */}
                        <div>
                            <AddressBlock label="Bill To" companyId={record.company_id}>
                                <div style={{ display: 'flex', gap: 4, marginTop: 6, paddingTop: 6, borderTop: '1px solid #ddd' }}>
                                    <span style={{ fontWeight: 700, minWidth: 90 }}>Contact Person:</span>
                                    <span>
                                        <ReferenceField source="customer_id" reference="contacts" link={false}>
                                            <TextField source="first_name" />{' '}<TextField source="last_name" />
                                        </ReferenceField>
                                    </span>
                                </div>
                            </AddressBlock>
                        </div>
                    </div>

                    {/* ── SHIP TO ── */}
                    <div className="ship-to-bar border-l border-r border-b border-black">
                        <AddressBlock 
                            companyId={record.company_id} 
                            useShipping 
                            label="Ship To" 
                        />
                    </div>

                    {/* ── DEAR SIR ── */}
                    <div className="dear-sir">
                        Dear Sir,<br />
                        <span style={{ marginLeft: 20 }}>Please find our Tax Invoice for the items listed below.</span>
                    </div>

                    {/* ── ITEMS TABLE ── */}
                    <table className="items-tbl">
                        <thead>
                            <tr>
                                <th style={{ width: 44 }}>SI No</th>
                                <th className="left">Description</th>
                                <th style={{ width: 80 }}>HSN / SAC</th>
                                <th style={{ width: 56 }}>GST %</th>
                                <th style={{ width: 68 }}>QTY</th>
                                <th style={{ width: 52 }}>UOM</th>
                                <th style={{ width: 100 }}>Rate</th>
                                <th style={{ width: 110 }}>Total Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {record.items?.map((item: any, index: number) => (
                                <tr key={index}>
                                    <td>{String(index + 1).padStart(5, '0')}</td>
                                    <td className="left"><div className="item-desc-main">{item.description}</div></td>
                                    <td>{item.hsn_code || '-'}</td>
                                    <td>{item.tax_rate ?? 18}%</td>
                                    <td style={{ fontWeight: 700 }}>{Intl.NumberFormat('en-IN').format(item.quantity)}</td>
                                    <td>{item.unit || 'Nos'}</td>
                                    <td className="right">{Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(item.unit_price)}</td>
                                    <td className="right" style={{ fontWeight: 700 }}>{Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(item.line_total)}</td>
                                </tr>
                            ))}
                             <tr className="print-hide"><td colSpan={8} style={{ height: 40, borderBottom: 'none' }} /></tr>
                        </tbody>
                    </table>

                    {/* ── TOTALS ── */}
                    <div className="totals-section">
                        <div className="banking-details">
                            <b>Banking Details :</b>
                            <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, color: '#333' }}>
                                {record.bank_details || 'Bank Name: HDFC BANK\nA/c No: 50200055555555\nIFSC: HDFC0000001'}
                            </div>
                            <div style={{ marginTop: 8, fontWeight: 700 }}>
                                <b>Amount In Words:</b><br />
                                <span style={{ fontWeight: 400 }}>{amountInWords(record.total_amount)}</span>
                            </div>
                        </div>
                        <div className="totals-right">
                            <div className="t-row">
                                <span className="t-row-lbl">Sub Total</span>
                                <span style={{ fontWeight: 700 }}>{inr(record.subtotal)}</span>
                            </div>
                            <div className="t-row">
                                <span className="t-row-lbl">GST @ 18.00%</span>
                                <span style={{ fontWeight: 700 }}>{inr(record.tax_amount)}</span>
                            </div>
                            {record.lr_amount > 0 && (
                                <div className="t-row">
                                    <span className="t-row-lbl">Freight / LR</span>
                                    <span style={{ fontWeight: 700 }}>{inr(record.lr_amount)}</span>
                                </div>
                            )}
                            <div className="t-row">
                                <span className="t-row-lbl">Round Off</span>
                                <span style={{ fontWeight: 700 }}>{inr(0)}</span>
                            </div>
                            <div className="grand-total-bar">
                                <span style={{ letterSpacing: '0.08em' }}>GRAND TOTAL</span>
                                <span style={{ fontSize: 14 }}>{inr(record.total_amount)}</span>
                            </div>
                            {record.paid_amount > 0 && (
                                <div className="t-row" style={{ backgroundColor: '#f0fdf4' }}>
                                    <span className="t-row-lbl">Amount Paid</span>
                                    <span style={{ fontWeight: 700, color: '#16a34a' }}>{inr(record.paid_amount)}</span>
                                </div>
                            )}
                            <div className="t-row" style={{ backgroundColor: '#fef2f2' }}>
                                <span className="t-row-lbl">Balance Due</span>
                                <span style={{ fontWeight: 700, color: '#dc2626' }}>
                                    {inr(Math.max(0, record.total_amount - (record.paid_amount || 0)))}
                                </span>
                            </div>
                        </div>
                    </div>



                    <div className="print-spacer" />

                    <div className="footer-group">
                        {/* ── T&C ── */}
                        {record.terms_conditions && (
                            <div className="tc-section">
                                <div className="tc-title">Terms &amp; Conditions :</div>
                                <TextField source="terms_conditions" />
                            </div>
                        )}

                        {/* ── SIGNATORY ── */}
                        <div className="signatory-section">
                            <div className="signatory-col">
                                <div className="signatory-space" />
                                <div className="signatory-lbl">Prepared By</div>
                            </div>
                            <div className="signatory-col">
                                <div className="signatory-space">
                                    {record.status !== 'draft' && config.enableAutomatedSignatures && (
                                        <>
                                            <img
                                                src="/img/rovasys-stamp.png"
                                                alt="Company Stamp"
                                                className="stamp-img"
                                            />
                                        </>
                                    )}
                                </div>
                                <div className="signatory-lbl">Authorised Signatory</div>
                                {record.status !== 'draft' && config.enableAutomatedSignatures && (
                                    <div style={{ fontSize: 11, color: '#555' }}>Rovasys Allied Works Pvt Ltd</div>
                                )}
                            </div>
                        </div>

                        <div className="po-footer-center">
                            System generated Document, Does not require any signature.
                        </div>
                        <div className="po-footer">
                            <span>Page: 1 / 1</span>
                            {config.showPrintTimestamp && (
                                <span>Print Date/Time: {new Date().toLocaleDateString('en-IN')} / {new Date().toLocaleTimeString('en-IN')}</span>
                            )}
                        </div>
                    </div>

                    {/* ── AUDIT TRAIL ── */}
                    {(record.approved_at || record.reminder_sent_at) && (
                        <div style={{ marginTop: 20, borderTop: '1px solid #eee', paddingTop: 10 }}>
                            <h5 style={{ fontSize: 10, fontWeight: 700, color: '#666', textTransform: 'uppercase', marginBottom: 4 }}>Document Audit Trail</h5>
                            <div style={{ fontSize: 9, color: '#888', display: 'flex', gap: 20 }}>
                                {record.approved_at && (
                                    <span>Approved On: {new Date(record.approved_at).toLocaleString('en-IN')}</span>
                                )}
                                {record.reminder_sent_at && (
                                    <span>Last Reminder: {new Date(record.reminder_sent_at).toLocaleString('en-IN')}</span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export const InvoiceShow = () => {
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [showWatermark, setShowWatermark] = useState(true);

    return (
        <Show title={<InvoiceShowTitle />} actions={<InvoiceShowActions onEmailClick={() => setEmailModalOpen(true)} onPaymentClick={() => setPaymentModalOpen(true)} showWatermark={showWatermark} onToggleWatermark={() => setShowWatermark(!showWatermark)} />}>
            <InvoiceShowContent showWatermark={showWatermark} />
            <InvoiceEmailModal open={emailModalOpen} onClose={() => setEmailModalOpen(false)} />
            <InvoicePaymentModal open={paymentModalOpen} onClose={() => setPaymentModalOpen(false)} />
        </Show>
    );
};